import React, { useState } from 'react';

import { TextButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import IconCalendar from '@components/Images/Rebranding/IconCalendar';
import LazyImgixImage from '@components/LazyImgixImage';
import { FEATURES } from '@components/pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import {
  PRO_SUBSCRIBERS_ONLY,
  SIGN_IN,
  SUBSCRIBERS_ONLY,
} from '@components/pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { new_session_path, static_privacy_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Newsletter } from '@utils/backend-api/newsletters';
import { FormLocation } from '@utils/enums';
import { transformToSnakeCase } from '@utils/formatting';
import { handleSubscriberRedirect } from '@utils/newsletterHelper';

import NewsletterAction from './NewsletterAction';

interface ContentProps {
  newsletter: Newsletter;
}

const Content = ({ newsletter }: ContentProps) => {
  const { initialCurrentUser, isGdprRequired } = useAppContext();
  const { trackEvent } = useEventTracking();
  const {
    accessLevel,
    afterSignUpUrl,
    author,
    cardImageUrl,
    currentUserSubscribed,
    description,
    frequency,
    hasPublishedArticles,
    id,
    isProOnly,
    isSubscriberOnly,
    landingPageDescription,
    landingPageHeading,
    name,
    offerCode,
    previewDays,
    readLatestPath,
  } = newsletter;
  const [currentSubscribeStatus, setCurrentSubscribeStatus] = useState(currentUserSubscribed);
  const currentUserPresent = !!initialCurrentUser;
  const isComingSoon = readLatestPath.includes(FEATURES) && !hasPublishedArticles;
  const currentLabel = isProOnly
    ? PRO_SUBSCRIBERS_ONLY
    : isSubscriberOnly
    ? SUBSCRIBERS_ONLY
    : previewDays > 0 && previewDays < 999999
    ? `${previewDays}-Day Free Trial`
    : undefined;

  const canSubscribeToNewsletter =
    initialCurrentUser?.access_level >= accessLevel || !isSubscriberOnly;

  return (
    <main className="mt-32 px-16 sm:mt-88 sm:px-28" data-testid="newsletter-landing-content">
      <div className="m-auto flex max-w-container-lg justify-center">
        <div className="m-auto flex w-full max-w-[1100px] flex-wrap-reverse justify-center gap-20 sm:flex-nowrap lg:gap-40">
          <div className="w-full sm:w-[500px]">
            {currentLabel && (
              <div className="mb-24 max-w-[182px] bg-primary-magenta py-4 text-center font-suisse-intl-mono text-overline-sm text-white">
                {currentLabel}
              </div>
            )}
            <h1 className="m-0 mb-16 font-suisse-screen text-headline-alt-md sm:mb-0 md:text-headline-alt-md">
              {landingPageHeading || name}
            </h1>
            <div className="font-suisse-screen text-byline-sm">By {author}</div>
            <p className="m-0 my-16 font-suisse-neue text-body-md md:text-body-lg">
              {landingPageDescription}
            </p>
            <div className="flex items-center gap-8 font-suisse-intl-mono text-overline-md">
              <IconCalendar />
              <span>{frequency}</span>
            </div>
            {currentUserPresent ? (
              <NewsletterAction
                afterSignUpUrl={afterSignUpUrl}
                author={author}
                canSubscribeToNewsletter={canSubscribeToNewsletter}
                currentUserSubscribed={currentSubscribeStatus}
                description={description}
                id={id}
                initialCurrentUser={initialCurrentUser}
                isCommingSoon={isComingSoon}
                isPro={isProOnly}
                name={name}
                offers={{
                  subscribe: offerCode,
                }}
                readLatestPath={readLatestPath}
                setCurrentSubscribeStatus={setCurrentSubscribeStatus}
              />
            ) : (
              <div className="my-24 w-full sm:max-w-[420px]">
                <EmailLeadCapture
                  buttonText="Get started"
                  formClassName="mb-8 sm:col-span-2"
                  formLocation={FormLocation.NewsletterSignup}
                  gdpr={isGdprRequired}
                  gdprClassName="mb-16"
                  inputClassName="w-full"
                  leadType={transformToSnakeCase(name) + `_newsletter_lead`}
                  newsletterIds={[Number(id)]}
                  redirectToOnSuccess={handleSubscriberRedirect({
                    afterSignUpUrl: afterSignUpUrl,
                    canSubscribeToNewsletter: canSubscribeToNewsletter,
                    isCurrentlySubscriber: initialCurrentUser?.is_subscriber,
                    isPro: isProOnly,
                    isProSubscriber: initialCurrentUser?.is_pro_subscriber,
                    offers: {
                      subscribe: offerCode,
                    },
                  })}
                  signInOptionClass="text-center sm:text-left"
                  toastMessageOnSuccess={
                    canSubscribeToNewsletter ? `Thank you for signing up for ${name}.` : null
                  }
                />

                <div className="font-suisse-screen text-overline-sm">
                  By providing your email, you agree to The Information&rsquo;s{' '}
                  <a className="text-primary-magenta" href={static_privacy_path()}>
                    Privacy Policy
                  </a>
                </div>
                <div className="mt-16 font-suisse-screen text-byline-sm">
                  <span>Already subscribed to The Information? </span>
                  <TextButton
                    as="link"
                    href={new_session_path()}
                    onClickCapture={() =>
                      trackEvent(click({ clickTarget: ClickTarget.SignIn, clickText: SIGN_IN }))
                    }
                    className="!inline-block !w-auto text-byline-sm text-primary-magenta no-underline"
                  >
                    Sign in here.
                  </TextButton>
                </div>
              </div>
            )}
          </div>
          <LazyImgixImage
            src={cardImageUrl}
            wrapperClassName="!flex items-start self-start"
            className="aspect-[16/9] w-full object-cover sm:w-[396px] lg:w-[468px]"
          />
        </div>
      </div>
    </main>
  );
};

export default Content;
